import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  extractPlainTextFromHtml,
  sanitizeHtmlContent,
  sanitizeTextContent,
} from 'src/utils/sanitizeContent'
import type {
  SeoContentAndFaqProcessedItem,
  SeoContentAndFaqProps,
  SeoContentAndFaqTheme,
} from 'src/types/seoContentAndFaq2'

import FaqSection20 from './FaqSection'

const COLLAPSED_LINES = 4

const THEME_STYLES = {
  light: {
    section: 'bg-white',
    title: 'text-fullDark',
    text: 'text-fullDark',
    button: 'text-fullDark',
    iconColor: '#101010',
  },
  dark: {
    section: 'bg-fullDark',
    title: 'text-white',
    text: 'text-tertiary [&_a]:text-white [&_u]:text-white',
    button: 'text-primaryBlue',
    iconColor: '#FFFFFF',
  },
} as const

export default function SeoContentAndFaq20({
  theme = 'light',
  title,
  text,
  faq,
  layout = 'default',
}: SeoContentAndFaqProps) {
  const normalizedTheme: SeoContentAndFaqTheme =
    theme === 'dark' ? 'dark' : 'light'

  const styles = THEME_STYLES[normalizedTheme]
  const textRef = useRef<HTMLDivElement | null>(null)

  const [isExpanded, setIsExpanded] = useState(false)
  const [isTextOverflowing, setIsTextOverflowing] = useState(false)
  const [isTextMeasured, setIsTextMeasured] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  const sanitizedTitle = useMemo(() => {
    return sanitizeHtmlContent(title)
  }, [title])

  const sanitizedText = useMemo(() => {
    return sanitizeHtmlContent(text)
  }, [text])

  const validFaqItems = useMemo<SeoContentAndFaqProcessedItem[]>(() => {
    return (faq?.faqItems ?? [])
      .map((item) => {
        const question = sanitizeTextContent(item?.question)
        const answer = sanitizeHtmlContent(item?.answer)
        const answerText = extractPlainTextFromHtml(answer)

        return {
          question,
          answer,
          answerText,
        }
      })
      .filter((item) => item.question && item.answerText)
  }, [faq?.faqItems])

  const hasValidFaq = validFaqItems.length > 0

  useEffect(() => {
    if (hasValidFaq) {
      setIsTextMeasured(true)
      setIsTextOverflowing(false)

      return
    }

    const textElement = textRef.current

    if (!textElement || !sanitizedText) {
      setIsTextMeasured(true)
      setIsTextOverflowing(false)

      return
    }

    const measureOverflow = () => {
      const currentTextElement = textRef.current

      if (!currentTextElement) {
        return
      }

      const lineHeight = Number.parseFloat(
        window.getComputedStyle(currentTextElement).lineHeight || '0'
      )

      const maxHeightByLines =
        lineHeight > 0
          ? lineHeight * COLLAPSED_LINES
          : currentTextElement.clientHeight

      const isOverflowing =
        currentTextElement.scrollHeight > maxHeightByLines + 1

      setIsTextOverflowing(isOverflowing)
      setIsTextMeasured(true)
    }

    measureOverflow()
    window.addEventListener('resize', measureOverflow)

    return () => {
      window.removeEventListener('resize', measureOverflow)
    }
  }, [hasValidFaq, sanitizedText])

  useEffect(() => {
    if (hasUserInteracted) {
      return
    }

    if (hasValidFaq) {
      setIsExpanded(false)

      return
    }

    if (!isTextMeasured) {
      return
    }

    setIsExpanded(!isTextOverflowing)
  }, [hasUserInteracted, hasValidFaq, isTextMeasured, isTextOverflowing])

  if (!sanitizedTitle && !sanitizedText && !hasValidFaq) {
    return null
  }

  const showReadMoreButton = hasValidFaq || isTextOverflowing

  const handleToggleExpanded = () => {
    setHasUserInteracted(true)
    setIsExpanded((previousValue) => !previousValue)
  }

  return (
    <section
      data-testid="seo-editorial-2-0"
      className={classNames('w-full', styles.section)}
    >
      <div
        className={classNames(
          'mx-auto w-full py-9 md:pt-[61px] md:pb-[50px]',
          layout === 'listing'
            ? 'max-w-[1680px] px-4 md:px-0 intermediate:px-[42px]'
            : 'max-w-[1550px] px-md md:px-[62px]'
        )}
      >
        <div className="md:pl-[22px] mb-[60px]">
          {sanitizedTitle && (
            <div
              data-testid="seo-editorial-title"
              className={classNames(
                'text-heading-sm font-extrabold max-w-[744px]',
                styles.title
              )}
              dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
            />
          )}

          {sanitizedText && (
            <div
              ref={textRef}
              data-testid="seo-editorial-text"
              className={classNames(
                'mt-4 text-sm max-w-[744px]',
                // Classe estática (não interpolada) para que o Tailwind JIT
                // consiga detectá-la via scan estático de código-fonte.
                // Deve permanecer sincronizada com COLLAPSED_LINES acima.
                !isExpanded && 'line-clamp-4',
                styles.text
              )}
              dangerouslySetInnerHTML={{ __html: sanitizedText }}
            />
          )}

          {showReadMoreButton && (
            <button
              type="button"
              data-testid="seo-editorial-read-more"
              aria-expanded={isExpanded}
              className={classNames(
                'mt-4 inline-flex items-center text-sm underline font-bold',
                styles.button,
                isExpanded && 'hidden'
              )}
              onClick={handleToggleExpanded}
            >
              ver mais
            </button>
          )}
        </div>

        {hasValidFaq && (
          <FaqSection20
            title={faq?.title ?? ''}
            items={validFaqItems}
            theme={normalizedTheme}
            isExpanded={isExpanded}
          />
        )}
      </div>
    </section>
  )
}
